// ============================================================
// useFeeStructure Hook
// ============================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSchool } from './useSchool';
import { feeService } from '@/services/feeService';

export function useFeeStructures() {
  const { currentSchool } = useSchool();

  return useQuery({
    queryKey: ['feeStructures', currentSchool?.id],
    queryFn: () => feeService.getFeeStructures(currentSchool.id),
    enabled: !!currentSchool?.id,
    select: (res) => res.data,
  });
}

export function useCreateFeeStructure() {
  const { currentSchool } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => feeService.createFeeStructure(currentSchool.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feeStructures', currentSchool?.id] });
    },
  });
}

export function useUpdateFeeStructure() {
  const { currentSchool } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => feeService.updateFeeStructure(currentSchool.id, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feeStructures', currentSchool?.id] });
    },
  });
}

export function useDeleteFeeStructure() {
  const { currentSchool } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => feeService.deleteFeeStructure(currentSchool.id, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feeStructures', currentSchool?.id] });
    },
  });
}

export function useAssignFees() {
  const { currentSchool } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => feeService.assignFees(currentSchool.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingFees', currentSchool?.id] });
    },
  });
}

export function useStudentFeeAssignments(skip = 0, limit = 100) {
  const { currentSchool } = useSchool();

  return useQuery({
    queryKey: ['studentFeeAssignments', currentSchool?.id, skip, limit],
    queryFn: () => feeService.getStudentFeeAssignments(currentSchool.id, skip, limit),
    enabled: !!currentSchool?.id,
    select: (res) => res.data,
  });
}

export function useAssignStudentFees() {
  const { currentSchool } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => feeService.assignStudentFees(currentSchool.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['studentFeeAssignments', currentSchool?.id] });
      queryClient.invalidateQueries({ queryKey: ['pendingFees', currentSchool?.id] });
      queryClient.invalidateQueries({ queryKey: ['assignableFeeStructures', currentSchool?.id] });
      queryClient.invalidateQueries({ queryKey: ['feeStructures', currentSchool?.id] });
    },
  });
}

export function useDeleteStudentFeeAssignment() {
  const { currentSchool } = useSchool();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => feeService.deleteStudentFeeAssignment(currentSchool.id, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['studentFeeAssignments', currentSchool?.id] });
      queryClient.invalidateQueries({ queryKey: ['pendingFees', currentSchool?.id] });
      queryClient.invalidateQueries({ queryKey: ['assignableFeeStructures', currentSchool?.id] });
    },
  });
}

export function useAssignableFeeStructures(skip = 0, limit = 100) {
  const { currentSchool } = useSchool();

  return useQuery({
    queryKey: ['assignableFeeStructures', currentSchool?.id, skip, limit],
    queryFn: () => feeService.getAssignableFeeStructures(currentSchool.id, skip, limit),
    enabled: !!currentSchool?.id,
    select: (res) => res.data,
  });
}
